import {
  first_present,
  normalize_key,
  normalize_text,
  to_number,
} from './company_cases_common';
import {
  getXVerdictSlaRuntimes,
  updateXVerdictSlaRuntimeStatus,
} from '@/services/xverdict_sla_service';

const BREACH_KEYS = ['overdue', 'breach'];
const WARNING_KEYS = ['left', 'warning', 'at-risk'];
const PRIMARY_RUNTIME_STATUSES = ['breached', 'escalated', 'warning', 'active'];
const FRONTEND_BREACHABLE_STATUSES = ['active', 'warning'];

function includes_key(value, keys) {
  return keys.some((key) => value.includes(key));
}

function runtime_priority(runtime = {}) {
  const status = normalize_text(runtime.status).toLowerCase();
  const index = PRIMARY_RUNTIME_STATUSES.indexOf(status);
  return index === -1 ? PRIMARY_RUNTIME_STATUSES.length : index;
}

function sort_runtimes(left = {}, right = {}) {
  const priority_difference = runtime_priority(left) - runtime_priority(right);
  if (priority_difference) return priority_difference;

  return Date.parse(right.started_at || 0) - Date.parse(left.started_at || 0);
}

function runtime_label(runtime = {}) {
  return normalize_text(first_present([
    runtime.badge_label,
    runtime.time_label,
    runtime.status_label,
    runtime.status,
  ]));
}

function runtime_class(runtime = {}, label = '') {
  const tone = normalize_key(runtime.tone_class);
  const normalized = normalize_key([runtime.status, label].filter(Boolean).join(' '));

  if (tone.includes('danger') || includes_key(normalized, BREACH_KEYS)) {
    return 'company-case-sla-badge is-danger';
  }

  if (tone.includes('warning') || includes_key(normalized, WARNING_KEYS)) {
    return 'company-case-sla-badge is-warning';
  }

  if (tone.includes('closed') || normalized.includes('closed')) {
    return 'company-case-sla-badge is-closed';
  }

  return 'company-case-sla-badge is-active';
}

function runtime_icon(runtime = {}, label = '') {
  const normalized = normalize_key([runtime.status, label].filter(Boolean).join(' '));
  return includes_key(normalized, BREACH_KEYS) ? 'bi-exclamation-triangle' : '';
}

function runtime_details(runtime = {}) {
  const label = runtime_label(runtime);
  return {
    sla: label || 'On track',
    sla_class: runtime_class(runtime, label),
    sla_icon: runtime_icon(runtime, label),
    sla_runtime: runtime,
  };
}

function default_sla_details(raw_sla, normalized) {
  return {
    sla: raw_sla || 'Queued',
    sla_class: includes_key(normalized, WARNING_KEYS)
      ? 'company-case-sla-badge is-warning'
      : 'company-case-sla-badge is-active',
    sla_icon: '',
  };
}

export function sla_details(record = {}) {
  if (record?.sla_runtime) {
    return runtime_details(record.sla_runtime);
  }

  const raw_sla = normalize_text([
    record?.sla,
    record?.sla_status,
    record?.sla_label,
    record?.due_status,
  ].find(Boolean));
  const normalized = normalize_key(raw_sla);
  if (includes_key(normalized, BREACH_KEYS)) return {
    sla: raw_sla || 'Decision overdue',
    sla_class: 'company-case-sla-badge is-danger',
    sla_icon: 'bi-exclamation-triangle',
  };

  return default_sla_details(raw_sla, normalized);
}

function sla_runtime_params(record = {}) {
  const application_id = to_number(first_present([
    record?.application_id,
    record?.latest_application_id,
    record?.current_application_id,
  ]));
  const entity_profile_id = to_number(first_present([
    record?.entity_profile_id,
    record?.id,
  ]));

  return {
    application_id,
    entity_profile_id: application_id ? null : entity_profile_id,
  };
}

async function primary_sla_runtime(record = {}) {
  const params = sla_runtime_params(record);
  if (!params.application_id && !params.entity_profile_id) return null;

  const runtimes = await getXVerdictSlaRuntimes(params);
  const runtime = runtimes.sort(sort_runtimes)[0] || null;
  return sync_frontend_breached_runtime(runtime);
}

function is_due_runtime(runtime = {}) {
  const status = normalize_text(runtime.status).toLowerCase();
  const due_time = Date.parse(runtime.due_at || '');
  return FRONTEND_BREACHABLE_STATUSES.includes(status)
    && Number.isFinite(due_time)
    && due_time <= Date.now();
}

async function sync_frontend_breached_runtime(runtime = null) {
  if (!runtime || !is_due_runtime(runtime)) return runtime;

  try {
    return await updateXVerdictSlaRuntimeStatus(runtime.id, 'breached') || {
      ...runtime,
      status: 'breached',
      status_label: 'overdue',
      tone_class: 'is-danger',
    };
  } catch {
    return {
      ...runtime,
      status: 'breached',
      status_label: 'overdue',
      tone_class: 'is-danger',
    };
  }
}

export async function with_sla_runtime(record = {}) {
  try {
    const runtime = await primary_sla_runtime(record);
    return runtime ? { ...record, ...runtime_details(runtime) } : record;
  } catch {
    return record;
  }
}
